import { useState } from 'react';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import FloatingADUWizard from '@/components/FloatingADUWizard';
import FloatingReserveButton from '@/components/FloatingReserveButton';
import CanonicalTag from '@/components/CanonicalTag';

const SpanishColonialADUDesignMilpitas = () => {
  const [showCalendly, setShowCalendly] = useState(false);

  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Spanish Colonial ADU Design and Construction in Milpitas",
    "description": "Professional Spanish Colonial ADU design and construction services in Milpitas, California. Custom accessory dwelling units with authentic architectural details.",
    "provider": {
      "@type": "Organization",
      "name": "UDA Construction"
    },
    "serviceArea": {
      "@type": "Place",
      "name": "Milpitas, California"
    }
  };

  return (
    <>
      <CanonicalTag url="https://udaconstruction.com/spanish-colonial-adu-design-milpitas" />
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      
      <Navigation />
      
      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg w-full max-w-4xl h-[80vh] relative border shadow-lg">
            <button
              onClick={closeCalendlyModal}
              className="absolute top-4 right-4 p-2 hover:bg-muted rounded-full z-10"
            >
              <X className="h-4 w-4" />
            </button>
            <iframe
              src="https://calendly.com/uda-construction"
              width="100%"
              height="100%"
              frameBorder="0"
              className="rounded-lg"
            />
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-background via-background to-muted">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
              Spanish Colonial ADU Design in Milpitas
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Create authentic Spanish Colonial accessory dwelling units in Milpitas with our expert design and construction services. Featuring red tile roofs, stucco walls, and timeless Mediterranean charm.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={openCalendlyModal}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Schedule Free Consultation
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                asChild
                className="border-border text-foreground hover:bg-muted"
              >
                <Link to="/adu-cost-calculator">Calculate ADU Cost</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Spanish Colonial */}
      <section className="py-16 bg-card">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-foreground mb-12">
              Why Choose Spanish Colonial ADU Design in Milpitas?
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-foreground mb-4">Timeless Architecture</h3>
                <p className="text-muted-foreground">
                  Red clay tile roofs, stucco exteriors, and wrought iron details create lasting appeal and authentic Spanish Colonial character.
                </p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-foreground mb-4">Climate-Adapted Design</h3>
                <p className="text-muted-foreground">
                  Thick walls, covered patios, and strategic windows optimize comfort in Milpitas' Mediterranean-like climate.
                </p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-foreground mb-4">Enhanced Property Value</h3>
                <p className="text-muted-foreground">
                  Spanish Colonial ADUs are highly sought after in California, providing excellent return on investment and rental potential.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-foreground mb-12">
              Spanish Colonial ADU Features
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-4">Authentic Exterior Elements</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Red clay tile roofing systems</li>
                  <li>• Smooth or textured stucco walls</li>
                  <li>• Wrought iron window grilles and balconies</li>
                  <li>• Arched doorways and windows</li>
                  <li>• Covered patios and courtyards</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-4">Interior Design Details</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Terra cotta or ceramic tile flooring</li>
                  <li>• Exposed wooden ceiling beams</li>
                  <li>• Built-in niches and alcoves</li>
                  <li>• Decorative tile backsplashes</li>
                  <li>• Warm earth-tone color palettes</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Local Considerations */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-foreground mb-12">
              Milpitas Spanish Colonial ADU Considerations
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-4">Local Regulations</h3>
                <p className="text-muted-foreground mb-4">
                  Milpitas allows ADUs up to 1,200 square feet or 60% of the primary dwelling, whichever is less. Spanish Colonial designs must comply with setback requirements while maintaining architectural integrity.
                </p>
                <p className="text-muted-foreground">
                  Our team ensures your Spanish Colonial ADU meets all Milpitas building codes and design standards.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-4">Climate & Materials</h3>
                <p className="text-muted-foreground mb-4">
                  Milpitas' Mediterranean climate is perfect for Spanish Colonial architecture. We select materials that withstand local weather patterns while maintaining authenticity.
                </p>
                <p className="text-muted-foreground">
                  Energy-efficient features integrate seamlessly with traditional Spanish Colonial aesthetics.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Section */}
      <section className="py-16 bg-card">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-foreground mb-8">
              Spanish Colonial ADU Investment in Milpitas
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Spanish Colonial ADUs typically range from $180,000 to $350,000 in Milpitas, depending on size, materials, and custom features. The authentic architectural details and quality craftsmanship provide excellent long-term value.
            </p>
            <Button 
              size="lg" 
              asChild
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Link to="/adu-cost-calculator">Get Detailed Cost Estimate</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary/10 to-secondary/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-foreground mb-6">
              Ready to Build Your Spanish Colonial ADU in Milpitas?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Let our experienced team create the perfect Spanish Colonial accessory dwelling unit for your Milpitas property. From design to completion, we handle every detail.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={openCalendlyModal}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Start Your Project Today
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                asChild
                className="border-border text-foreground hover:bg-muted"
              >
                <Link to="/portfolio">View Our Portfolio</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Related Links */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-foreground mb-12">
              Explore More ADU Design Options
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link 
                to="/mediterranean-adu-design-milpitas" 
                className="block p-6 bg-card rounded-lg border hover:border-primary transition-colors"
              >
                <h3 className="text-xl font-semibold text-foreground mb-3">Mediterranean ADU Design</h3>
                <p className="text-muted-foreground">Elegant Mediterranean-style ADUs with coastal charm and luxury features.</p>
              </Link>
              <Link 
                to="/craftsman-adu-design-milpitas" 
                className="block p-6 bg-card rounded-lg border hover:border-primary transition-colors"
              >
                <h3 className="text-xl font-semibold text-foreground mb-3">Craftsman ADU Design</h3>
                <p className="text-muted-foreground">Handcrafted details and natural materials in classic Craftsman style.</p>
              </Link>
              <Link 
                to="/modern-farmhouse-adu-design-milpitas" 
                className="block p-6 bg-card rounded-lg border hover:border-primary transition-colors"
              >
                <h3 className="text-xl font-semibold text-foreground mb-3">Modern Farmhouse ADU</h3>
                <p className="text-muted-foreground">Contemporary comfort meets rustic charm in modern farmhouse design.</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingADUWizard />
      <FloatingReserveButton />
    </>
  );
};

export default SpanishColonialADUDesignMilpitas;